class DrawerSearchSection extends HTMLElement {
  constructor() {
    super();

    this.isOpen = false;
    this.drawerClass = "wt-drawer-search";
    this.drawer = document.querySelector(`.${this.drawerClass}`);
    this.drawerNav = document.querySelector('.wt-drawer--nav');
    this.classDrawerActive = `${this.drawerClass}--active`;
    this.pageOverlayClass = "search-overlay";
    this.activeOverlayBodyClass = `${this.pageOverlayClass}-on`;
    this.drawerNavAnimationOffClass = 'wt-drawer--off-animations';
    this.body = document.body;

    this.triggerQuery = [
      ".wt-header__search-trigger",
      ".wt-header__search__close",
      ".search-results__terms",
    ].join(", ");
    this.triggers = () => document.querySelectorAll(this.triggerQuery);

    // search stuff
    this.cachedResults = {};
    this.input = this.querySelector('input[name="q"]');
    this.clearButton = this.querySelector(".wt-header__search__clear-button");
    this.closeButton = this.querySelector(".wt-header__search__close");
    this.predictiveSearchResults = this.querySelector(
      "[data-predictive-search]",
    );
    this.searchProperties = this.dataset.searchProperties;

    this.mainTrigger = this.querySelector(".wt-header__search-trigger");
    this.emptyAnnouncement = this.querySelector(".search-empty");
    this.searchResultTop = this.querySelector(".search-result-top");

    this.isVisibleClearButton = false;

    this.saveSuggestionMenuInDesignMode =
      this.saveSuggestionMenuInDesignMode.bind(this);

    this.isPredictiveSearchAvailable = this.hidePredictiveSearch();
    this.isPredictiveSearchEnabled = this.dataset.enablePredictiveSearch === 'true';
    this.showSuggestOnNoResults = this.dataset.showSuggestOnNoResults === 'true';

    // Lux: greeklish rescue ("krevati" finds κρεβάτι). Set when a Latin query with zero
    // results was answered by its Greek transliteration; form submit then carries it.
    this.luxAltTerm = null;
    this.luxAltResultsText = this.dataset.altResultsText || '%s';

    // Lux local engine (lux-search-engine.js): index-backed instant search with
    // typo tolerance + our ranking. The server predictive path below stays as
    // the fallback while the index loads or if it fails.
    this.luxEngine = null;
    this.luxLabels = {
      cash: this.dataset.cashLabel || "",
      regular: this.dataset.regularLabel || "",
      inStock: this.dataset.inStockText || "",
      products: this.dataset.tProducts || "",
      collections: this.dataset.tCollections || "",
      articles: this.dataset.tArticles || "",
      searchFor: this.dataset.searchForText || "%s",
      noResults: this.dataset.noResultsText || "%s",
    };
    this.luxStarsTemplate = this.querySelector("template[data-lux-stars]");

    this.setupEventListeners();

    this.toggleTabindexElements = [this.input, this.closeButton];

    this.suggestionMenuContent = this.querySelector(".search-result-top").cloneNode(true);
    this.timeOut = null;
    this.init();
  }


  getFocusableElements() {
    const focusableElementsSelector =
      "button, [href], input:not([type='hidden']), select, [tabindex]";
    const focusableElements = () =>
      Array.from(this.querySelectorAll(focusableElementsSelector)).filter(
        (el) => !el.hasAttribute("disabled") && el.tabIndex >= 0,
      );

    return {
      focusableElements,
      first: focusableElements()[0],
      last: focusableElements()[focusableElements().length - 1],
    };
  }

  // fixOverlayIssue() {
  //     const body = this.body;
  //
  //     if(body.classList.contains(this.activeOverlayBodyClass)) {
  //         const offsetTop = -parseInt(body.style.top);
  //         body.style.position = 'initial';
  //         body.style.top = 'initial';
  //         body.style.left = 'initial';
  //         window.scrollTo(0, offsetTop);
  //
  //     } else {
  //         // body.style.top = -document.documentElement.scrollTop+'px';
  //         // body.style.left = '0px';
  //         // body.style.position = 'fixed';
  //     }
  // }

  onToggle() {
    if (this.hasAttribute("open")) {
      this.removeAttribute("open");
      setTabindex(this.toggleTabindexElements, "-1");
      setTabindex([this.mainTrigger], "0");
      this.isOpen = false;
      setTimeout(() => {
          this.mainTrigger.focus();
        }, 0);
    } else {
      this.setAttribute("open", "");
      setTabindex(this.toggleTabindexElements, "0");
      setTabindex([this.mainTrigger], "-1");
      this.input.focus();
      this.input.setSelectionRange(
        this.input.value.length,
        this.input.value.length,
      );
      this.isOpen = true;
    }
  }

  toggleDrawerClasses() {
    const opening = !this.body.classList.contains(this.activeOverlayBodyClass);

    this.onToggle();
    this.drawer.classList.toggle(this.classDrawerActive);
    this.drawerNav?.classList.toggle(this.drawerNavAnimationOffClass);
    this.body.classList.toggle(this.activeOverlayBodyClass);

    if (opening) {
      this.armCascade(this.drawer);
      this.ensureLuxEngine();
    }
  }

  ensureLuxEngine() {
    if (this.luxEngine || !window.LuxSearch || !this.dataset.indexUrl) return;
    let storage = null;
    try { storage = window.sessionStorage; } catch (e) { /* blocked: no cache */ }
    this.luxEngine = new window.LuxSearch.LuxSearchEngine(this.dataset.indexUrl, storage);
    this.luxEngine.load().then(() => {
      // Index arrived while the shopper was already typing: take over.
      const term = this.getQuery();
      if (term.length && this.isOpen) this.onChange();
    }).catch(() => { /* engine failed: server path keeps serving */ });
  }

  armCascade(root) {
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;

    const items = root.querySelectorAll("[data-cascade] .scroll-trigger");
    if (items.length === 0) return;

    let idx = 0;
    items.forEach((item) => {
      if (item.classList.contains("search-result__title")) idx = 0;
      item.classList.add("scroll-trigger--offscreen");
      item.style.setProperty("--animation-order", idx);
      idx += 1;
    });

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        items.forEach((item) =>
          item.classList.remove("scroll-trigger--offscreen"),
        );
      });
    });
  }

  init() {
    this.triggers().forEach((trigger) => {
      trigger.addEventListener("click", (e) => {
        e.preventDefault();
        this.toggleDrawerClasses();
      });
    });

    this.clearButton.addEventListener("click", () => {
      this.input.value = "";
      this.clearButton.style.display = "none";
      this.isVisibleClearButton = false;
      this.clearResults();
    });

    this.addEventListener("keydown", (e) => {
      const isTabPressed =
        e.key === "Tab" || e.keyCode === 9 || e.code === "Tab";
      const { first, last } = this.getFocusableElements();

      if (e.key === "Escape" || e.keyCode === 27 || e.code === "Escape") {
        if (this.isOpen) {
          this.toggleDrawerClasses();
        }
      }

      if (isTabPressed) {
        if (this.isOpen) {
          if (e.shiftKey && document.activeElement === first) {
            last.focus();
            e.preventDefault();
          } else if (!e.shiftKey && document.activeElement === last) {
            first.focus();
            e.preventDefault();
          }
        }
      }
    });

    this.input.addEventListener("input", () => {
      if (this.input.value.length > 0 && !this.isVisibleClearButton) {
        this.clearButton.style.display = "flex";
        this.clearButton.setAttribute("tabindex", "0");
        this.isVisibleClearButton = true;
      } else if (this.input.value.length === 0 && this.isVisibleClearButton) {
        this.clearButton.style.display = "none";
        this.clearButton.setAttribute("tabindex", "-1");
        this.isVisibleClearButton = false;
      }
    });
  }

  hidePredictiveSearch() {
    // when predictive search is not available, we need to hide the predictive search results
    const featuresTag = document.getElementById("shopify-features");
    if (featuresTag) {
      const featuresData = JSON.parse(featuresTag.textContent);
      if (featuresData.predictiveSearch === false) {
        return false;
      }
    }
    return true;
  }

  // search stuff
  setupEventListeners() {
    const form = this.querySelector("form.store-search-form");
    form.addEventListener("submit", this.onFormSubmit.bind(this));
    if (this.isPredictiveSearchAvailable && this.isPredictiveSearchEnabled) {
      // Ghost completion (desktop only): the field completes to the nearest
      // collection title, remainder selected so the next keystroke replaces it.
      // Runs before the debounced search, which then searches the prediction.
      if (window.matchMedia("(hover: hover) and (pointer: fine)").matches) {
        this.input.setAttribute("aria-autocomplete", "both");
        this.luxPrevTypedLen = 0;
        this.input.addEventListener("input", () => {
          const typed = this.input.value;
          const grew = typed.length > this.luxPrevTypedLen;
          this.luxPrevTypedLen = typed.length;
          if (!grew || !this.luxEngine || !this.luxEngine.ready) return;
          if (this.input.selectionStart !== typed.length) return;
          const comp = this.luxEngine.complete(typed);
          if (!comp) return;
          this.input.value = comp.text;
          this.input.setSelectionRange(typed.length, comp.text.length);
          this.luxPrevTypedLen = typed.length;
        });
      }

      this.input.addEventListener(
        "input",
        debounce((event) => {
          this.onChange(event);
        }, 180).bind(this),
      );
    }

    if (Shopify.designMode) {
      document.addEventListener(
        "shopify:section:load",
        this.saveSuggestionMenuInDesignMode,
      );
    }
  }

  saveSuggestionMenuInDesignMode() {
    if (this.body.classList.contains(this.activeOverlayBodyClass))
      this.drawer.classList.toggle(this.classDrawerActive);
  }

  getQuery() {
    return this.input.value.trim();
  }

  onChange() {
    const searchTerm = this.getQuery();
    this.luxAltTerm = null;

    if (searchTerm.length) {
      if (this.luxEngine && this.luxEngine.ready) {
        this.renderLuxResults(searchTerm);
      } else {
        this.getSearchResults(searchTerm);
      }
      this.removeAttribute("empty");
    } else {
      this.clearResults();
    }
  }

  onFormSubmit(event) {
    if (
      !this.getQuery().length ||
      this.querySelector('[aria-selected="true"] a')
    ) {
      event.preventDefault();
      return;
    }
    // Lux: when the visible results came from the greeklish rescue, submit that
    // working term so the full results page is not a dead end.
    if (this.luxAltTerm) this.input.value = this.luxAltTerm;
  }

  fetchResults(searchTerm) {
    const queryKey = searchTerm.replace(" ", "-").toLowerCase();

    if (this.cachedResults[queryKey]) {
      return Promise.resolve(this.cachedResults[queryKey]);
    }

    return fetch(
      `${routes.predictive_search_url}?q=${encodeURIComponent(searchTerm)}&${encodeURIComponent("resources[type]")}=product,page,article,collection,query&${encodeURIComponent("resources[limit_scope]")}=each&${encodeURIComponent("resources[limit]")}=6&${encodeURIComponent("resources[options][fields]")}=${encodeURIComponent(this.searchProperties)}&section_id=predictive-search`,
    )
      .then((response) => {
        if (!response.ok) {
          let error = new Error(response.status);
          throw error;
        }
        return response.text();
      })
      .then((text) => {
        const resultsMarkup = new DOMParser()
          .parseFromString(text, "text/html")
          .querySelector("#shopify-section-predictive-search").innerHTML;
        this.cachedResults[queryKey] = resultsMarkup;
        return resultsMarkup;
      });
  }

  getSearchResults(searchTerm) {
    if (!this.isPredictiveSearchEnabled) return;
    this.setLiveRegionLoadingState();

    this.fetchResults(searchTerm)
      .then((resultsMarkup) => {
        this.renderSearchResults(resultsMarkup);

        if (this.hasRenderedResults()) return;

        const altTerm = this.luxGreeklish(searchTerm);
        if (!altTerm) return;

        return this.fetchResults(altTerm).then((altMarkup) => {
          // The input may have changed while the rescue fetch ran.
          if (this.getQuery() !== searchTerm) return;
          this.renderSearchResults(altMarkup);
          if (this.hasRenderedResults()) {
            this.luxAltTerm = altTerm;
            this.injectAltNote(altTerm);
          }
        });
      })
      .catch((error) => {
        // Predictive search rejects some buyer locales (Hebrew: 401 on the
        // section fetch, 417 on the raw endpoint — measured 2026-07-11).
        // Stop fetching for the session; the curated showroom stands in.
        if (error && (error.message === "401" || error.message === "417")) {
          this.isPredictiveSearchEnabled = false;
        }
        this.renderSearchResults("");
      });
  }

  hasRenderedResults() {
    // Scoped to the server-rendered container: the no-results showroom clone
    // (appended as a sibling) reuses the same group classes and must not count.
    return Boolean(
      this.predictiveSearchResults.querySelector(
        "#predictive-search-results .search-result-products, #predictive-search-results .search-result-pages",
      ),
    );
  }

  injectAltNote(altTerm) {
    const note = document.createElement("p");
    note.className = "lux-search-note";
    note.textContent = this.luxAltResultsText.replace("%s", altTerm);
    this.predictiveSearchResults.prepend(note);
  }

  // -- Lux engine rendering: same ids/classes/roles as sections/predictive-search.liquid

  luxEsc(s) {
    return String(s).replace(/[&<>"']/g, (ch) => ({
      "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;",
    })[ch]);
  }

  // Bold the query words inside a title (whole matched words, calm weight-only style).
  luxHighlight(title, terms) {
    const internals = window.LuxSearch._internals;
    const variants = [];
    internals.splitWords(terms).forEach((tok) => {
      variants.push(internals.fold(tok));
      const gr = internals.greeklish(tok);
      if (gr) variants.push(internals.fold(gr));
    });
    if (!variants.length) return this.luxEsc(title);
    return String(title).split(/([^\p{L}\p{N}]+)/u).map((part) => {
      const w = internals.fold(internals.norm(part));
      if (!w || !/[\p{L}\p{N}]/u.test(part)) return this.luxEsc(part);
      const hit = variants.some((tv) => {
        if (w === tv || w.indexOf(tv) === 0) return true;
        let i = 0;
        const n = Math.min(w.length, tv.length);
        while (i < n && w[i] === tv[i]) i++;
        return i >= 3 && i >= Math.min(w.length, tv.length) - 1;
      });
      return hit ? '<strong class="highlight">' + this.luxEsc(part) + "</strong>" : this.luxEsc(part);
    }).join("");
  }

  luxPriceMarkup(p) {
    const esc = this.luxEsc.bind(this);
    if (p.pw) {
      return '<div class="price"><div class="price__container"><div class="price__lux">'
        + '<span class="lux-price__row">'
        + '<span class="visually-hidden visually-hidden--inline">' + esc(this.luxLabels.regular) + "</span>"
        + '<s class="price-item price-item--regular lux-price__was">' + esc(p.pw) + "</s>"
        + '<span class="visually-hidden visually-hidden--inline">' + esc(this.luxLabels.cash) + "</span>"
        + '<span class="price-item wt-product__price__final lux-price__cash-amount">' + esc(p.pc) + "</span>"
        + '<span class="lux-price__cash-label" aria-hidden="true">' + esc(this.luxLabels.cash) + "</span>"
        + "</span></div></div></div>";
    }
    return '<div class="price"><div class="price__container"><div class="price__regular">'
      + '<span class="visually-hidden visually-hidden--inline">' + esc(this.luxLabels.regular) + "</span>"
      + '<span class="price-item price-item--regular wt-product__price__final">' + esc(p.pc) + "</span>"
      + "</div></div></div>";
  }

  luxStarsMarkup(rating) {
    if (!rating || !this.luxStarsTemplate) return "";
    // Mirror rating-stars.liquid: stars = rating, gap = max(ceil(stars)-1,0)*2,
    // fill = (stars*12 + gap) / 68 * 100
    const gap = Math.max(Math.ceil(rating) - 1, 0) * 2;
    const fill = ((rating * 12 + gap) / 68) * 100;
    return this.luxStarsTemplate.innerHTML.replace(/--fill-pct:\s*[^;"]+/, "--fill-pct: " + fill + "%");
  }

  luxProductItem(p, terms) {
    const esc = this.luxEsc.bind(this);
    let thumb = "";
    if (p.img) {
      thumb = '<figure class="wt-cart__item__thumb suggest-item__thumb">'
        + '<img src="' + esc(p.img) + '" alt="' + esc(p.t) + '" loading="lazy" width="200" height="200">'
        + "</figure>";
    }
    return '<a href="' + esc(p.u) + '" class="suggest-item">' + thumb
      + '<div class="wt-cart__item__body suggest-item__body"><div class="wt-cart__item__data">'
      + '<h3 class="wt-cart__item__name suggest-item__name">' + this.luxHighlight(p.t, terms) + "</h3>"
      + '<div class="wt-cart__item__price suggest-item__price">' + this.luxPriceMarkup(p) + "</div>"
      + (p.av ? '<span class="lux-stock suggest-item__stock">' + esc(this.luxLabels.inStock) + "</span>" : "")
      + this.luxStarsMarkup(p.r)
      + "</div></div></a>";
  }

  luxLinkGroup(title, items, terms) {
    if (!items.length) return "";
    const cascade = ' class="search-result-pages__item scroll-trigger animate--slide-in disabled-on-mobile"';
    return '<div class="search-result__title scroll-trigger animate--slide-in disabled-on-mobile">' + this.luxEsc(title) + "</div>"
      + '<ul class="search-result-pages__list">'
      + items.map((c) => "<li" + cascade + '><a href="' + this.luxEsc(c.u) + '">' + this.luxHighlight(c.t, terms) + "</a></li>").join("")
      + "</ul>";
  }

  luxSearchForButton(terms, extraClass, tabindex) {
    const label = this.luxLabels.searchFor.replace("%s", terms);
    return '<div id="predictive-search-option-search-keywords" class="search-result-more' + extraClass + '" role="option">'
      + '<button aria-label="' + this.luxEsc(label) + '" class="search-result-more__link" tabindex="' + tabindex + '">'
      + this.luxEsc(label) + "</button></div>";
  }

  renderLuxResults(searchTerm) {
    const res = this.luxEngine.search(searchTerm);
    if (!res) return;
    const terms = res.terms;
    const total = res.products.length + res.collections.length + res.articles.length;

    if (total === 0) {
      // renderSearchResults handles the results attr, the live-region text and
      // the no-results showroom append.
      this.renderSearchResults(
        '<span class="" data-predictive-search-live-region-count-value>'
        + this.luxEsc(this.luxLabels.noResults.replace("%s", searchTerm)) + "</span>",
      );
      return;
    }

    let productsGroup = "";
    if (res.products.length) {
      productsGroup = '<div class="search-result-products">'
        + '<div id="predictive-search-products" class="search-result__title scroll-trigger animate--slide-in disabled-on-mobile">' + this.luxEsc(this.luxLabels.products) + "</div>"
        + '<ul id="predictive-search-results-list" class="search-result-list" role="listbox" aria-labelledby="predictive-search-products">'
        + res.products.map((p, i) =>
          '<li id="predictive-search-option-' + (i + 1) + '" class="search-result-list__item scroll-trigger animate--slide-in disabled-on-mobile" role="option" aria-selected="false">'
          + this.luxProductItem(p, terms) + "</li>").join("")
        + "</ul>"
        + this.luxSearchForButton(terms, " search-result-more--desktop", "0")
        + "</div>";
    }

    let metaGroup = "";
    if (res.articles.length) {
      metaGroup = '<div class="search-result-pages">'
        + this.luxLinkGroup(this.luxLabels.articles, res.articles, terms)
        + "</div>";
    }

    // Collections lead as a slim pills row (Tsoulfa: predicted intents first),
    // photos start immediately after.
    let chips = "";
    if (res.collections.length) {
      chips = '<nav class="lux-search-chips" aria-label="' + this.luxEsc(this.luxLabels.collections) + '">'
        + res.collections.map((c) =>
          '<a href="' + this.luxEsc(c.u) + '">' + this.luxHighlight(c.t, terms) + "</a>").join("")
        + "</nav>";
    }

    const markup = chips
      + '<div id="predictive-search-results" class="search-result" data-cascade>'
      + productsGroup + metaGroup + "</div>"
      + this.luxSearchForButton(terms, "", "-1");

    this.predictiveSearchResults.innerHTML = markup;
    if (res.corrected) {
      this.luxAltTerm = res.corrected;
      this.injectAltNote(res.corrected);
    }
    this.setAttribute("results", true);
    this.setLiveRegionResults();
    this.armCascade(this.predictiveSearchResults);
  }

  // Greeklish → Greek (digraphs first, then singles, word-final sigma).
  // Returns null when the query is not convertible Latin text.
  luxGreeklish(term) {
    if (!/[a-z]/i.test(term)) return null;
    if (/[α-ωά-ώϊϋΐΰ]/i.test(term)) return null;

    const digraphs = [
      [/th/g, "θ"],
      [/ch/g, "χ"],
      [/ps/g, "ψ"],
      [/ks/g, "ξ"],
    ];
    const singles = {
      a: "α", b: "μπ", c: "κ", d: "δ", e: "ε", f: "φ", g: "γ",
      h: "η", i: "ι", k: "κ", l: "λ", m: "μ", n: "ν", o: "ο",
      p: "π", r: "ρ", s: "σ", t: "τ", u: "υ", v: "β", w: "ω",
      x: "χ", y: "υ", z: "ζ", "8": "θ",
    };

    let out = term.toLowerCase();
    digraphs.forEach(([re, gr]) => {
      out = out.replace(re, gr);
    });
    out = out.replace(/[a-z8]/g, (ch) => singles[ch] || ch);
    out = out.replace(/σ\b/g, "ς");

    if (!/[α-ω]/.test(out) || out === term.toLowerCase()) return null;
    return out;
  }

  setLiveRegionLoadingState() {
    this.statusElement =
      this.statusElement || this.querySelector(".predictive-search-status");
    this.loadingText =
      this.loadingText || this.getAttribute("data-loading-text");

    this.setLiveRegionText(this.loadingText);
    this.setAttribute("loading", true);
  }

  setLiveRegionText(statusText) {
    this.statusElement =
      this.statusElement || this.querySelector(".predictive-search-status");
    this.statusElement.setAttribute("aria-hidden", "false");
    this.statusElement.textContent = statusText || "";

    this.timeOut = setTimeout(() => {
      this.statusElement.setAttribute("aria-hidden", "true");
    }, 1000);
  }

  renderSearchResults(resultsMarkup) {
    this.predictiveSearchResults.innerHTML = resultsMarkup;

    if (this.showSuggestOnNoResults) {
      const hasResults = this.predictiveSearchResults.querySelector(
        ".search-result-products, .search-result-pages",
      );
      if (!hasResults) {
        this.predictiveSearchResults.appendChild(
          this.suggestionMenuContent.cloneNode(true),
        );
      }
    }

    this.setAttribute("results", true);

    this.setLiveRegionResults();

    this.armCascade(this.predictiveSearchResults);
  }

  setLiveRegionResults() {
    this.removeAttribute("loading");
    this.setLiveRegionText(
      this.querySelector("[data-predictive-search-live-region-count-value]")
        ?.textContent,
    );
  }

  clearResults() {
    this.input.value = "";
    this.luxAltTerm = null;
    this.removeAttribute("results");
    this.setAttribute("empty", true);
    this.statusElement.innerHTML = this.suggestionMenuContent.innerHTML || '';

    clearTimeout(this.timeOut);
    setTimeout(() => {
      this.statusElement.removeAttribute("aria-hidden");
      this.armCascade(this.statusElement);
    }, 300);
  }
}

customElements.define("search-drawer", DrawerSearchSection);
